import Pagination from "rc-pagination";
import React, {
  ComponentPropsWithRef,
  PropsWithChildren,
  useEffect,
  useState,
} from "react";
import Icon from "src/components/icon";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSearch,
} from "src/components";
import { titleCase } from "src/common/utils";

// This only caters for offline/client side table
export interface TableHeaderData {
  label: string;
  renderColumnAs?: (e: any) => React.ReactNode;
  dataKey?: string; // Target the key directly from data, display value directly if "renderColumnAs" is not used
  cellClass?: string[];
}
interface Props extends PropsWithChildren<ComponentPropsWithRef<"table">> {
  data: any;
  headerData: TableHeaderData[];
  searchPlaceholder?: string;
  searchTargetKey?: string[];
  hasPagination?: boolean;
}
type Ref = HTMLDivElement;

const TableUtility = React.forwardRef<Ref, Props>((props, ref) => {
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const { className } = props;

  useEffect(() => {
    paginationChange(1);
  }, [search]);

  const paginationChange = (page: number) => {
    setCurrentPage(page);
  };

  const paginate = () => {
    const paginatedList = props.data.filter((datum: any) =>
      (props.searchTargetKey ?? []).some((key) =>
        String(datum[key as keyof typeof datum])
          .toLowerCase()
          .includes(search.toLowerCase())
      )
    );

    return {
      total: paginatedList.length,
      slice: paginatedList.slice((currentPage - 1) * 20).slice(0, 20),
    };
  };
  const result = paginate();

  const prevNextArrow = (
    current: number,
    type: string,
    originalElement: React.ReactNode
  ) => {
    if (type === "prev") {
      return (
        <button title="Previous page">
          <Icon name="chevron" className="w-3 h-3" />
        </button>
      );
    }
    if (type === "next") {
      return (
        <button title="Next page">
          <Icon name="chevron" className="w-3 h-3 rotate-180" />
        </button>
      );
    }
    return originalElement;
  };

  return (
    <>
      {props.searchTargetKey && props.searchTargetKey.length > 0 && (
        <TableSearch
          placeholder={
            props.searchPlaceholder ??
            `Search by ${props.searchTargetKey
              .map((t) => titleCase(t))
              .join(" / ")}`
          }
          onSearchInput={(e: string) => {
            setSearch(e);
            paginationChange(1);
          }}
        />
      )}
      <Table className={className} ref={ref}>
        <TableHead>
          <TableRow>
            {props.headerData.map((datum, index) => (
              <TableCell isHeadCell key={`t_header_${index}`}>
                {datum.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {result.slice.length ? (
            result.slice.map((a: any, index_a: Number) => (
              <TableRow key={`t_row_${index_a.toString()}`}>
                {props.headerData.map((b: TableHeaderData, index_b: Number) => (
                  <TableCell
                    className={b.cellClass?.join(" ")}
                    key={`t_cell_${index_a.toString()}.${index_b.toString()}`}
                  >
                    {b.renderColumnAs?.(a) ?? (b.dataKey ? a[b.dataKey] : "")}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={props.headerData.length}>
                <div className="flex justify-center">No Data</div>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {props.hasPagination ? (
        <Pagination
          showTotal={(total, range) =>
            `Showing ${range[0]}-${range[1]} of ${total}`
          }
          onChange={paginationChange}
          total={result.total}
          current={currentPage}
          pageSize={20}
          itemRender={prevNextArrow}
          hideOnSinglePage={true}
        />
      ) : null}
    </>
  );
});

export default TableUtility;
